import { useEffect, useRef, useState } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useOrganization, useUser } from '@clerk/react';
import { toast } from 'sonner';
import { GoogleSheetsIcon } from '@/components/ui/icons/google-product-icons';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { followingUserText } from './question-card.util';
import {
  googlePickError,
  useGooglePicker,
} from '@/hooks/use-google-picker.hook';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatContext,
  selectAiChatConversationId,
  selectAiChatSessions,
  sendAiChatMessage,
} from '@/store/features/ui.feature';

const SHEETS_LOGO = (
  <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
    <GoogleSheetsIcon className='size-[12px]' />
  </div>
);

/** Grants the linked file for a Clu read, without creating a lead list. */
export function GoogleSheetReadCard({
  result,
  toolCallId,
}: {
  result: Record<string, unknown>;
  toolCallId: string;
}) {
  const { organization, isLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const context = useAppSelector(selectAiChatContext);
  const conversation = useAppSelector(selectAiChatConversationId);
  const sessions = useAppSelector(selectAiChatSessions);
  // Two plain values, not one object: useAuiState is useSyncExternalStore,
  // and a new object on every read never settles ("Maximum update depth").
  const state = {
    message: useAuiState((aui) => aui.message.id),
    available: useAuiState((aui) => {
      const tools = aui.message.content.filter(
        (part) => part.type === 'tool-call'
      );
      return (
        followingUserText(aui.thread.messages, aui.message.id) === null &&
        aui.thread.messages.at(-1)?.id === aui.message.id &&
        tools.at(-1)?.toolCallId === toolCallId
      );
    }),
  };
  if (
    !isLoaded ||
    !userLoaded ||
    !organization ||
    !user ||
    result.organization_id !== organization.id ||
    result.google_user_id !== user.id
  )
    return null;
  const scope = JSON.stringify([
    organization.id,
    user.id,
    context,
    conversation,
    sessions?.activeKey,
    state.message,
    state.available,
    toolCallId,
    result.spreadsheet_id,
    result.gid,
  ]);
  return <ReadCard key={scope} result={result} enabled={state.available} />;
}

function ReadCard({
  result,
  enabled,
}: {
  result: Record<string, unknown>;
  enabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const { canPick, pick } = useGooglePicker();
  const live = useRef(true);
  const busyRef = useRef(false);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const spreadsheet =
    typeof result.spreadsheet_id === 'string' &&
    /^[A-Za-z0-9_-]{10,200}$/.test(result.spreadsheet_id)
      ? result.spreadsheet_id
      : null;
  const gid =
    typeof result.gid === 'number' &&
    Number.isInteger(result.gid) &&
    result.gid >= 0
      ? result.gid
      : null;
  /** False unlocks the button: nothing was sent to Clu. */
  const read = async () => {
    if (!enabled || !canPick || !spreadsheet || sent || busyRef.current)
      return false;
    busyRef.current = true;
    try {
      const selected = await pick(spreadsheet);
      if (!live.current || !selected || selected.id !== spreadsheet)
        return false;
      setSent(true);
      dispatch(
        sendAiChatMessage(
          `Read cell values from https://docs.google.com/spreadsheets/d/${spreadsheet}/edit${gid === null ? '' : `#gid=${gid}`}`
        )
      );
      return true;
    } catch (error) {
      if (live.current) toast.error(googlePickError(error));
      return false;
    } finally {
      busyRef.current = false;
    }
  };
  if (sent || !enabled)
    return (
      <AgentCard
        leading={SHEETS_LOGO}
        title='Read Google Sheet'
        trailing={
          sent ? (
            <CardStatus tone='success'>Shared with Clu</CardStatus>
          ) : (
            <CardStatus>Passed over</CardStatus>
          )
        }
      />
    );
  const fact = canPick ? 'Opens with Google' : 'Google Sheets not connected';
  return (
    <AgentCard leading={SHEETS_LOGO} title='Read Google Sheet'>
      {canPick && spreadsheet ? (
        <CardFooter
          control={<CardMeta className='min-w-0'>{fact}</CardMeta>}
          primary={{
            label: 'Read sheet',
            workingLabel: 'Opening',
            onClick: read,
          }}
        />
      ) : (
        <CardMeta>{fact}</CardMeta>
      )}
    </AgentCard>
  );
}
